import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, CircularProgress, IconButton, SxProps } from "@mui/material";
import { useState } from "react";
import { useAddStudentToTheoryLessonMutation } from "../api/backoffice.api";
import { StudentSelect } from "../inputs/StudentSelect";
import { TheoryLesson } from "../model/autovioCalendarEvents";
import { Student } from "../providers/studentsProvider";
import { Row } from "../misc/Row";
import { reportError } from "../backoffice.utils";

export function AddStudentToTheoryLessonButton(props: {
  sx?: SxProps;
  theoryLesson: TheoryLesson;
  buttonLabel?: string;
  onAddStudent?: (student: Student) => void;
  onAddStudentError?: (student: Student) => void;
}) {
  const { sx, theoryLesson, buttonLabel = "Fahrschüler hinzufügen", onAddStudent, onAddStudentError } = props;
  const [showStudentSelect, setShowStudentSelect] = useState(false);
  const [addingStudent, setAddingStudent] = useState(false);
  const addStudentToTheoryLessonMutation = useAddStudentToTheoryLessonMutation();

  const addStudentToTheoryLesson = async (student: Student) => {
    try {
      onAddStudent?.(student);
    } catch (error) {
      reportError("onAddStudent callback failed", error);
    }
    setAddingStudent(true);
    await runSafely(
      () =>
        addStudentToTheoryLessonMutation.mutateAsync({
          studentUid: student.id,
          calendarEventUid: theoryLesson.id,
        }),
      {
        errorMessage: `Fehler beim Hinzufügen von ${student.callName}.`,
        successMessage: `${student.callName} erfolgreich hinzugefügt.`,
        onError: () => onAddStudentError?.(student),
      },
    );
    setAddingStudent(false);
    setShowStudentSelect(false);
  };

  const handleStudentSelected = async (student: Student | null) => {
    if (!student) return;
    await addStudentToTheoryLesson(student);
  };

  return showStudentSelect ? (
    <Row sx={sx} alignItems="center" spacing="10px">
      <StudentSelect
        autoFocus
        label="Fahrschüler hinzufügen"
        disabled={addingStudent}
        sx={{ height: "40px", minWidth: "250px", flex: 1 }}
        size="small"
        onChange={(student) => handleStudentSelected(student)}
        drivingSchoolId={theoryLesson.drivingSchoolId}
        filterStudents={(student) => !student.archived}
      />
      {addingStudent ? (
        <CircularProgress size={24} />
      ) : (
        <IconButton size="small" onClick={() => setShowStudentSelect(false)}>
          <CloseIcon color="action" />
        </IconButton>
      )}
    </Row>
  ) : (
    <Box sx={sx}>
      <Button
        variant="outlined"
        sx={{ height: "40px" /* ... same height as <StudentSelect> */ }}
        startIcon={<AddIcon />}
        onClick={() => setShowStudentSelect(true)}
      >
        {buttonLabel}
      </Button>
    </Box>
  );
}
